import { T, useT } from '@transifex/react';
import { t } from '@transifex/native';
import { redirect } from 'react-router';
import { env } from 'cloudflare:workers';
import { parseWithZod } from '@conform-to/zod';
import { SentryServer } from '~/lib/sentry.server';

import type { components } from '~/api/openapi';

import { Login, loginSchema } from '~/components/auth/login';
import { emailSchema } from '~/lib/validation';
import { loginWithMfa, ssoDiscover } from './login.server';
import { handleFormErrors } from '~/lib/handleFormErrors';
import { handleActionError } from '~/api/error-handler';
import { sessionStorage, getSession } from '~/lib/session';
import { AuthPageLayout } from '~/components/auth/auth-page-layout';
import { guestOnlyMiddleware } from '~/middleware/guest-only';
import type { Route } from './+types/login';

import { sentryActionPerfMiddleware } from '~/middleware/sentry';

export const middleware = [sentryActionPerfMiddleware, guestOnlyMiddleware];

type MfaChallengeData = components['schemas']['MfaChallengeResponseDto'];

export async function action({ request, context }: Route.ActionArgs) {
  const url = new URL(request.url);
  const next = url.searchParams.get('next');
  const formData = await request.formData();
  const intent = formData.get('intent');

  // ── SSO Discover ─────────────────────────────────────────────────────────
  if (intent === 'discover') {
    const submission = parseWithZod(formData, { schema: emailSchema });
    if (submission.status !== 'success') {
      return submission.reply();
    }

    // In local dev, Vite sits behind a TLS-terminating proxy (Caddy), so url.origin
    // reflects `http://` even when the browser used HTTPS. Consult X-Forwarded-Proto
    // only in development; deployed envs use url.origin verbatim.
    const redirectUri =
      env.ENVIRONMENT === 'development'
        ? `${request.headers.get('x-forwarded-proto') ?? url.protocol.replace(':', '')}://${url.host}/auth/sso/callback`
        : `${url.origin}/auth/sso/callback`;
    const result = await ssoDiscover(submission.value.email, redirectUri, context);

    if (result.error) {
      const formError = handleFormErrors(result, submission);
      if (formError) return formError;

      if (result.response.status === 502) {
        return submission.reply({
          formErrors: [
            t(
              "Your organization's SSO provider is temporarily unavailable. Try again in a moment."
            ),
          ],
        });
      }

      if (result.response.status === 400) {
        return submission.reply({
          formErrors: [t('Please check your email and try again.')],
        });
      }

      await handleActionError(result, context);
    }

    const discoveredMethod = result.data?.method;
    const authorizationUrl = result.data?.authorizationUrl;

    if (discoveredMethod === 'OPENID_SSO') {
      if (!authorizationUrl) {
        throw new Response('SSO misconfigured: missing authorization URL', { status: 500 });
      }
      const session = await getSession(request);
      if (next) {
        session.set('ssoNext', next);
      } else {
        session.unset('ssoNext');
      }
      throw redirect(authorizationUrl, {
        headers: { 'Set-Cookie': await sessionStorage.commitSession(session) },
      });
    }

    // PASSWORD: stash email in session, redirect to step 2
    const session = await getSession(request);
    session.set('ssoLoginEmail', submission.value.email);
    const params = new URLSearchParams({ step: 'password' });
    if (next) params.set('next', next);
    throw redirect(`/login?${params.toString()}`, {
      headers: { 'Set-Cookie': await sessionStorage.commitSession(session) },
    });
  }

  // ── Login (intent=login or legacy no-intent) ──────────────────────────────
  const submission = parseWithZod(formData, { schema: loginSchema });
  if (submission.status !== 'success') {
    return submission.reply();
  }

  const { email, password } = submission.value;

  try {
    const response = await loginWithMfa(email, password, context);

    if (response.response?.status === 400 || response.response?.status === 401) {
      return submission.reply({
        fieldErrors: { password: [t('Invalid login credentials. Please try again.')] },
      });
    }

    if (response.error) {
      await handleActionError(response, context);
    }

    const session = await getSession(request);

    const { challengeToken, mfaMethod, expiresAt } =
      (response.data as MfaChallengeData | undefined) || {};
    if (!challengeToken || !mfaMethod || !expiresAt) {
      return submission.reply({
        formErrors: [t('Invalid login response.')],
      });
    }

    session.set('mfaLoginChallenge', {
      challengeToken,
      mfaMethod,
      expiresAt,
      next: next ?? undefined,
    });
    session.set('email', email);
    session.unset('accessToken');
    session.unset('refreshToken');
    session.unset('accountType');
    session.unset('userId');
    session.unset('ssoLoginEmail');

    return redirect('/login/verify', {
      headers: { 'Set-Cookie': await sessionStorage.commitSession(session) },
    });
  } catch (error: unknown) {
    SentryServer.captureException(error);
    return error;
  }
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const session = await getSession(request);
  const ssoLoginEmail = session.get('ssoLoginEmail');

  // Guard: step=password requires a stashed email (prevents deep-link to step 2)
  if (url.searchParams.get('step') === 'password' && !ssoLoginEmail) {
    return redirect('/login');
  }

  return { ssoLoginEmail: ssoLoginEmail ?? null };
}

export default function LoginPage() {
  const t = useT();

  return (
    <>
      <title>{t('Login - Startable')}</title>
      <AuthPageLayout
        illustration={
          <img
            src="/assets/auth/login.svg"
            alt=""
            className="size-full object-cover object-center"
          />
        }
      >
        <h2 className="text-h1 text-text-900">
          <T _str="Log In" />
        </h2>
        <Login />
      </AuthPageLayout>
    </>
  );
}
